import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import { format, getYear, isAfter } from "date-fns";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { editMode } from "../../../atoms/profileScreenEdit";
import { useGetReleaseGenreText } from "../../../hooks/releaseHooks/useGetReleaseGenreText";
import { Release } from "../../../store/models/release";
import { getDisplayableNameForUser } from "../../../store/utils/entityUtils";
import { isLiveSimple } from "../../../utils/releases/dateUtils";
import { Text } from "../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../core-ui/components/Text/TextUtils";
import { EditReleaseButton } from "../FeaturedArtistRelease/EditReleaseButton";
import { FeatureReleasePill } from "../FeaturedArtistRelease/FeaturedReleasePill";
import { ReleasePlayPauseButton } from "../ReleasePlayPauseButton/ReleasePlayPauseButton";
import {
  ReleaseItemDetailsContainer,
  ReleaseListContainerStyle,
  ReleaseListItemContainer,
} from "./ReleaseList.styles";
import { ReleaseListItemCoverPhoto } from "./ReleaseListItemCoverPhoto";

interface ReleaseListItemProps {
  release: Release;
  onReleaseClick: (releaseId?: number) => void;
}

const ReleaseListItem = ({ release, onReleaseClick }: ReleaseListItemProps) => {
  // Temporarily hide play count
  // const { data: releaseDetails, isLoading: isReleaseDetailsInitialLoading } =
  //   useFetchReleaseDetailsQuery(release?.id);

  const theme = useTheme();
  const isEditMode = useAtomValue(editMode);

  const { isLive, publishedDate, publishedEnd } = useMemo(() => {
    const { published_at, published_end } = release;
    const isLive = isLiveSimple(published_at, published_end);
    const publishedDate = published_at ? new Date(published_at) : null;
    const publishedEnd = published_end ? new Date(published_end) : null;

    return { isLive, publishedDate, publishedEnd };
  }, [release]);

  const notLiveText = useMemo(() => {
    const now = new Date();
    if (!publishedDate || (publishedEnd && isAfter(now, publishedEnd))) {
      return "Not Live";
    }
    return format(publishedDate, "MM/dd/yyyy hh:mm a");
  }, [publishedDate, publishedEnd]);

  const genreText = useGetReleaseGenreText(
    release.supply_chain_main_genre,
    release.supply_chain_secondary_genre,
  );

  return (
    <ReleaseListItemContainer
      onClick={() => {
        onReleaseClick(release.id);
      }}
    >
      <ReleaseListItemCoverPhoto
        releaseCover={release.release_cover}
        hasShadow={false}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ReleasePlayPauseButton releaseId={release.id} />
        </Box>
      </ReleaseListItemCoverPhoto>
      <ReleaseItemDetailsContainer>
        <EditReleaseButton releaseId={release.id}>
          <Typography
            textAlign={"left"}
            lineHeight={"20px"}
            fontStyle={"normal"}
            fontWeight={"600"}
            flexWrap={"wrap"}
            maxWidth={"150px"}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            fontSize={"16px"}
            sx={{
              margin: "0 !important",
            }}
          >
            {release.title}
          </Typography>
        </EditReleaseButton>

        <Text variant={TextStyleVariant.P3}>
          {release.artist_display_name ||
            `${getDisplayableNameForUser(release.artist_user)}`}
          {release.published_at
            ? ` (${getYear(new Date(release.published_at))})`
            : ""}
        </Text>
        <Text variant={TextStyleVariant.P3}>{genreText}</Text>
        {/* Temporarily hide play count */}
        {/* {isReleaseDetailsInitialLoading ? (
          <Skeleton style={{ marginTop: 8 }} height={5} width={100} />
        ) : (
          <Text
            style={{
              color: theme.palette.secondary.light,
              marginTop: 8,
            }}
            variant={TextStyleVariant.P4}
          >{`${releaseDetails?.total_play} plays`}</Text>
        )} */}
        {isEditMode && (
          <FeatureReleasePill
            color={isLive ? theme.palette.active : theme.palette.pending}
            text={isLive ? "Live Now" : notLiveText}
            sx={(theme) => ({
              borderRadius: theme.border.radius.xxs,
              height: "unset",
              marginTop: theme.spacing(0.5),
              minWidth: "unset",
              padding: theme.spacing(0, 0.5),
              width: "fit-content",
            })}
          />
        )}
      </ReleaseItemDetailsContainer>
    </ReleaseListItemContainer>
  );
};

const LoadingReleaseListItem = () => {
  return (
    <ReleaseListItemContainer>
      <Skeleton variant={"rounded"} width={"64px"} height={"64px"} />
      <ReleaseItemDetailsContainer>
        <Skeleton height={10} width={200} />
        <Skeleton height={5} width={200} />
      </ReleaseItemDetailsContainer>
    </ReleaseListItemContainer>
  );
};

const ReleaseListItemSkeleton = () => {
  return (
    <ReleaseListContainerStyle>
      {Array.from({ length: RELEASES_PAGE_SIZE }).map((_, idx) => (
        <LoadingReleaseListItem key={idx} />
      ))}
    </ReleaseListContainerStyle>
  );
};

interface ReleaseListProps {
  releases: Release[];
  isPending: boolean;
  onReleaseClick: (releaseId?: number) => void;
}

export const ReleaseList = ({
  releases,
  isPending,
  onReleaseClick,
}: ReleaseListProps) => {
  if (isPending) {
    return <ReleaseListItemSkeleton />;
  }
  return (
    <ReleaseListContainerStyle>
      {releases.map((release) => (
        <ReleaseListItem
          key={release.id}
          release={release}
          onReleaseClick={onReleaseClick}
        />
      ))}
    </ReleaseListContainerStyle>
  );
};

// This value must be keep in sync with BE
// engineears/release/release_services/release_service.py
export const RELEASES_PAGE_SIZE = 9;
